// @ flow
import React, {useEffect, useState, useRef} from "react";
import {useHistory} from "react-router-dom";
import {Button, Descriptions, Row, Skeleton, Col, Tag, Tooltip} from "antd";
import {
    EditOutlined,
    CheckOutlined,
    CloseOutlined,
    LockOutlined,
    SafetyCertificateFilled
} from "@ant-design/icons";
import {useTranslation} from "react-i18next";
import {Link} from "react-router-dom";

import {useStore} from "../../../hooks/useStore";
import {useFetch} from "../../../hooks/useFetch";
import {formatPhoneNumber, isConstantExist, getConstant} from "../../../utils/helpers";
import {userTypes} from "../../../constants/constants";
import {USER_TICKET_VIEWER_ROLES} from "../../../constants/ticket";
import {ConditionalTooltip} from "../../_defaultComponents/ConditionalTooltip/ConditionalTooltip";
import {CustomButton} from "../../_defaultComponents/CustomButton/CustomButton";
import {ChangePasswordModal} from "../../_defaultComponents/ChangePasswordModal/ChangePasswordModal";
import {pagePermissions, permissionKey} from "../../../constants/pagePermissions";

// "sayfa.aksiyon" anahtarlarını sayfa bazında gruplar. Sıralama ve kaynak; NewUser formundaki
// PagePermissionCard ile aynı registry (constants/pagePermissions.js) olduğundan, hem sayfalar
// hem de her sayfanın aksiyonları oradaki tanım sırasıyla listelenir. Registry'den kaldırılmış
// eski/artık geçersiz sayfa ya da aksiyon anahtarları (örn. önceden verilmiş ama sonradan
// kaldırılmış bir yetki) burada da elenir — aksi halde çevirisi bulunamayıp ham anahtar olarak
// ("pagePermissions.pages.xyz") görünürlerdi.
const groupPagePermissionsByPage = (grantedKeys = []) => {
    const pages = {};
    Object.keys(pagePermissions).forEach(page => {
        const validActions = ["view", ...pagePermissions[page].actions];
        const grantedActions = validActions.filter(action =>
            grantedKeys.includes(permissionKey(page, action))
        );
        if (grantedActions.length > 0) {
            pages[page] = grantedActions;
        }
    });
    return pages;
};

export const UserDetail = ({userId}) => {
    const stores = useStore();
    const history = useHistory();
    const {t} = useTranslation(["userDetail", "userTypes", "pagePermissions", "tickets"]);
    const [recall, setRecall] = useState(true);
    const [companies, setCompanies] = useState([{name: "", _id: ""}]);
    const [subCompanies, setSubCompanies] = useState([{name: "", _id: ""}]);
    const [visibleChangePasswordModal, setVisibleChangePasswordModal] = useState(false);
    const [showPagePermissionsDetail, setShowPagePermissionsDetail] = useState(false);

    const recallRef = useRef(recall);
    recallRef.current = recall;

    const [fieldManagerCompanies, setFieldManagerCompanies] = useState("");
    const [fieldManagerSubCompanies, setFieldManagerSubCompanies] = useState("");
    const [loading, userData, setData, setLoading] = useFetch({
        fetchFn: stores.userStore.getUser.bind(null, {
            userId
        }),
        dependencies: [recall]
    });

    const getAccessibleFields = async userId => {
        const result = await stores.userStore.getAccessibleFields({
            userId
        });

        if (result) {
            setFieldManagerCompanies(result.data.companies?.map(c => c.companyName).join(", "));
            setFieldManagerSubCompanies(result.data.subcompanies?.map(c => c.name).join(", "));
        }
    };

    useEffect(() => {
        if (userData && userData?.data && userData?.data?.role === "fieldManager") {
            getAccessibleFields(userId);
        }
    }, [userData]);

    useEffect(() => {
        if (userData.status && userData.status !== 200) {
            stores.appStore.openErrorModal({
                content: userData.data.message,
                onOk: () => {
                    history.push("/users");
                }
            });
        } else {
            setLoading(true);
            setCompanies(
                userData?.data?.companies
                    ? userData?.data?.companies.map(c => ({
                          ...c,
                          name: c.companyName
                      }))
                    : []
            );
            setSubCompanies(userData?.data?.subcompanies ? userData?.data?.subcompanies : []);
            setLoading(false);
        }
    }, [userData]);

    const updateUserDisable = () => {
        const {disabled} = userData.data;
        stores.appStore.openConfirmModal({
            title: t("modal.confirmTitle", {ns: "common"}),
            content: t("userDetail.modal.updateUserDisable.content", {
                ns: "userDetail",
                disabledStatus: disabled
                    ? t("other.activeLowercase", {ns: "common"})
                    : t("other.inactiveLowercase", {ns: "common"})
            }),
            onOk: async () => {
                setLoading(true);
                let updatedUser = await stores.userStore.updateUser({
                    user: {...userData.data, disabled: !disabled},
                    disableUpdate: true
                });

                if (updatedUser) {
                    setRecall(!recallRef.current);
                }

                setLoading(false);
            }
        });
    };
    const updateUserPassword = async values => {
        const {newPassword} = values;
        await setRecall(!recallRef.current);
        setVisibleChangePasswordModal(!visibleChangePasswordModal);
        await stores.userStore.changeUserPassword({
            userId,
            password: newPassword
        });
    };

    const renderReferenceInfo = ({reference}) => {
        if (!reference) return "-";

        return (
            <>
                <p>
                    {t("fields.username", {ns: "common"})} :{" "}
                    <Link
                        to={{pathname: `/users/${reference?.panelUserId?.toString()}`}}
                        target="_blank"
                    >
                        {reference?.username}
                    </Link>
                </p>
                <p>
                    {t("fields.fullname", {ns: "common"})} : {reference?.fullName}
                </p>
                <p>
                    {t("fields.email", {ns: "common"})} : {reference?.email}
                </p>
                <p>
                    {t("fields.phoneNumber", {ns: "common"})} :{" "}
                    {formatPhoneNumber(reference?.phone)}
                </p>
                <p>Kod : {reference?.code}</p>
            </>
        );
    };

    // "Yetkili Olduğu Sayfalar": özet satırda yalnızca sayfa adları, "Detay Gör" ile sayfa
    // başına verilen aksiyonlar açılır (bkz. constants/pagePermissions.js).
    const renderPagePermissions = pagePermissionKeys => {
        const grantedPages = groupPagePermissionsByPage(pagePermissionKeys);
        const pageKeys = Object.keys(grantedPages);

        if (pageKeys.length === 0) return "-";

        return (
            <>
                {!showPagePermissionsDetail && (
                    <span data-testid="pagePermissionsSummary">
                        {pageKeys
                            .map(page =>
                                t(`pagePermissions.pages.${page}`, {ns: "pagePermissions"})
                            )
                            .join(", ")}{" "}
                    </span>
                )}
                <Button
                    type="link"
                    size="small"
                    data-testid="pagePermissionsDetailToggle"
                    onClick={() => setShowPagePermissionsDetail(prev => !prev)}
                >
                    {showPagePermissionsDetail ? "Detayı Gizle" : "Detay Gör"}
                </Button>
                {showPagePermissionsDetail && (
                    <div style={{marginTop: 8}}>
                        {pageKeys.map(page => (
                            <div key={page} style={{marginBottom: 6}}>
                                <strong>
                                    {t(`pagePermissions.pages.${page}`, {
                                        ns: "pagePermissions"
                                    })}
                                    :
                                </strong>{" "}
                                {grantedPages[page].map(action => (
                                    <Tag key={action}>
                                        {t(`pagePermissions.actionLabels.${page}.${action}`, {
                                            ns: "pagePermissions",
                                            defaultValue: t(`pagePermissions.actions.${action}`, {
                                                ns: "pagePermissions"
                                            })
                                        })}
                                    </Tag>
                                ))}
                            </div>
                        ))}
                    </div>
                )}
            </>
        );
    };

    // Acente kullanıcıyı görüntüler ve düzenler; yetkilendirme, şifre ve pasife alma işlemleri kapalıdır.
    const isAgency = stores.meStore.checkRole("isAgency");
    // Salt okunur roller kullanıcı detayını yalnızca görüntüler; yetkilendirme, şifre, düzenleme ve pasife alma kapalıdır.
    const isReadOnlyViewer = USER_TICKET_VIEWER_ROLES.includes(stores.meStore.role);
    const detailRole = userData?.data?.role;
    const canShowPasswordReset =
        !isAgency &&
        !isReadOnlyViewer &&
        detailRole &&
        (["cUser", "sOperation"].includes(detailRole) ||
            (["sChef", "sy"].includes(detailRole) &&
                ["admin", "superadmin"].includes(stores.meStore.role)));

    return (
        <div data-testid="userDetailScreen">
            {loading || !userData.data ? (
                <Skeleton paragraph={{rows: 2, width: "100%"}}></Skeleton>
            ) : (
                <>
                    <ChangePasswordModal
                        title={t("userDetail.modal.userResetPasswordModalTitle", {
                            ns: "userDetail"
                        })}
                        isVisible={visibleChangePasswordModal}
                        updateCourierPassword={values => updateUserPassword(values)}
                        setVisible={() => {
                            setVisibleChangePasswordModal(!visibleChangePasswordModal);
                        }}
                    />
                    <Row gutter={[0, 20]}>
                        <Col span={24}>
                            <Descriptions
                                title={
                                    <Row type="flex" justify="space-between">
                                        {userData.data.fullName}
                                        <ConditionalTooltip
                                            text={t("userDetail.disabledUpdateUserTooltip", {
                                                ns: "userDetail"
                                            })}
                                            condition={userData.data.disabled}
                                            child={
                                                <div>
                                                    {!isAgency && !isReadOnlyViewer && (
                                                        <Tooltip
                                                            title={t("userDetail.authorization", {
                                                                ns: "userDetail"
                                                            })}
                                                        >
                                                            <CustomButton
                                                                type="primary"
                                                                data-testid="authorizationButton"
                                                                href={
                                                                    "/users/authorization/" + userId
                                                                }
                                                                disabled={userData.data.disabled}
                                                                onClick={event => {
                                                                    event.preventDefault();
                                                                    history.push(
                                                                        "/users/authorization/" +
                                                                            userId
                                                                    );
                                                                }}
                                                                icon={<SafetyCertificateFilled />}
                                                            />
                                                        </Tooltip>
                                                    )}
                                                    {canShowPasswordReset && (
                                                        <CustomButton
                                                            type="primary"
                                                            disabled={userData.data.disabled}
                                                            onClick={() =>
                                                                setVisibleChangePasswordModal(true)
                                                            }
                                                            icon={<LockOutlined />}
                                                        />
                                                    )}
                                                    {!isReadOnlyViewer &&
                                                        stores.meStore.checkPermission(
                                                            "users.edit"
                                                        ) && (
                                                            <CustomButton
                                                                type="primary"
                                                                data-testid="editButton"
                                                                href={"/users/edit/" + userId}
                                                                disabled={userData.data.disabled}
                                                                onClick={event => {
                                                                    event.preventDefault();
                                                                    history.push(
                                                                        "/users/edit/" + userId
                                                                    );
                                                                }}
                                                                icon={<EditOutlined />}
                                                            />
                                                        )}
                                                </div>
                                            }
                                        />
                                    </Row>
                                }
                                column={1}
                                bordered
                            >
                                <Descriptions.Item label={t("fields.username", {ns: "common"})}>
                                    <span data-testid="username">{userData.data.username}</span>
                                </Descriptions.Item>
                                <Descriptions.Item label="E-mail">
                                    <span data-testid="email">{userData.data.email}</span>
                                </Descriptions.Item>
                                {stores.meStore.checkRole("notVisibleForCompanyUser") && (
                                    <Descriptions.Item label={t("fields.userRole", {ns: "common"})}>
                                        <span data-testid="role">
                                            {userData.data.role &&
                                                (isConstantExist(userTypes, userData.data.role)
                                                    ? t(
                                                          getConstant(userTypes, userData.data.role)
                                                              .translation_key,
                                                          {
                                                              ns: "userTypes"
                                                          }
                                                      )
                                                    : "-")}
                                        </span>
                                    </Descriptions.Item>
                                )}

                                {["cUser", "oUser", "sOperation", "sChef"].includes(
                                    userData.data.role
                                ) && (
                                    <>
                                        <Descriptions.Item
                                            label={t("fields.company", {ns: "common"})}
                                        >
                                            <span data-testid="company">
                                                {userData.data.auth?.companies.length > 0
                                                    ? companies
                                                          .map(company => company.name)
                                                          .join(", ")
                                                    : t("userDetail.noCompanyAdded", {
                                                          ns: "userDetail"
                                                      })}
                                            </span>
                                        </Descriptions.Item>
                                        <Descriptions.Item
                                            label={t("fields.subcompany", {ns: "common"})}
                                        >
                                            <span data-testid="subcompany">
                                                {userData.data.auth?.subcompanies.length > 0
                                                    ? subCompanies
                                                          .map(company => company.name)
                                                          .join(", ")
                                                    : t("userDetail.noSubcompanyAdded", {
                                                          ns: "userDetail"
                                                      })}
                                            </span>
                                        </Descriptions.Item>
                                    </>
                                )}
                                {["fieldManager"].includes(userData.data.role) && (
                                    <>
                                        <Descriptions.Item
                                            label={t("fields.company", {ns: "common"})}
                                        >
                                            <span data-testid="company">
                                                {fieldManagerCompanies !== ""
                                                    ? fieldManagerCompanies
                                                    : t("userDetail.noCompanyAdded", {
                                                          ns: "userDetail"
                                                      })}
                                            </span>
                                        </Descriptions.Item>
                                        <Descriptions.Item
                                            label={t("fields.subcompany", {ns: "common"})}
                                        >
                                            <span data-testid="subcompany">
                                                {fieldManagerSubCompanies !== ""
                                                    ? fieldManagerSubCompanies
                                                    : t("userDetail.noSubcompanyAdded", {
                                                          ns: "userDetail"
                                                      })}
                                            </span>
                                        </Descriptions.Item>
                                    </>
                                )}

                                <>
                                    {["cUser", "sOperation", "sChef"].includes(
                                        userData.data.role
                                    ) && (
                                        <Descriptions.Item
                                            label={t("fields.title", {ns: "common"})}
                                        >
                                            <span data-testid="title">{userData.data.title}</span>
                                        </Descriptions.Item>
                                    )}
                                    <Descriptions.Item
                                        label={t("fields.phoneNumber", {ns: "common"})}
                                    >
                                        <span data-testid="phoneNumber">
                                            {formatPhoneNumber(userData.data.phone)}
                                        </span>
                                    </Descriptions.Item>
                                </>
                                {["bm"].includes(userData?.data?.role) && (
                                    <Descriptions.Item label={"Yetkili Olduğu Bölge Müdürlüğü"}>
                                        <span data-testid="zoneManagement">
                                            {userData?.data?.zoneManagement?.name || "-"}
                                        </span>
                                    </Descriptions.Item>
                                )}
                                {["bm", "sy", "ik", "agency", "agencyIk", "subAgency"].includes(
                                    userData?.data?.role
                                ) && (
                                    <Descriptions.Item label={"Yetkili Olduğu Bölgeler"}>
                                        <span data-testid="zones">
                                            {userData?.data?.zones
                                                ?.map(zone => zone?.name)
                                                .join(", ") || "-"}
                                        </span>
                                    </Descriptions.Item>
                                )}
                                {["bm", "sy", "ik"].includes(userData?.data?.role) && (
                                    <Descriptions.Item label={"Denetleme Bölgeleri"}>
                                        <span data-testid="auditZones">
                                            {userData?.data?.auditZones
                                                ?.map(zone => zone?.name)
                                                .join(", ") || "-"}
                                        </span>
                                    </Descriptions.Item>
                                )}
                                <Descriptions.Item label={"Yetkili Olduğu Şehirler(varsa)"}>
                                    <span data-testid="cities">
                                        {[
                                            ...new Set([
                                                ...(userData?.data?.auth?.cities || []),
                                                ...(userData?.data?.zoneCities || [])
                                            ])
                                        ]
                                            .map(city =>
                                                typeof city === "string" ? city : city?.city
                                            )
                                            .filter(Boolean)
                                            .map(city => city.capitalize())
                                            .join(", ") || "-"}
                                    </span>
                                </Descriptions.Item>

                                <Descriptions.Item
                                    label={t("userDetail.fields.userActivity", {ns: "userDetail"})}
                                >
                                    <span data-tesid="userActivity">
                                        {userData.data.disabled
                                            ? t("other.inactive", {ns: "common"})
                                            : t("other.active", {ns: "common"})}
                                    </span>
                                </Descriptions.Item>
                                {stores.meStore.checkRole("isAdmin") && (
                                    <Descriptions.Item label={"Uat Bilgisi"}>
                                        <span data-testid="userUat">
                                            {userData?.data?.uat
                                                ? t("other.active", {ns: "common"})
                                                : "Pasif"}
                                        </span>
                                    </Descriptions.Item>
                                )}
                                <Descriptions.Item label={"Test Kullanıcı"}>
                                    <span data-testid="testUser">
                                        {userData?.data?.isTest
                                            ? t("other.yes", {ns: "common"})
                                            : t("other.no", {ns: "common"})}
                                    </span>
                                </Descriptions.Item>
                                <Descriptions.Item label={"Kuryeleri Arayabilir"}>
                                    <span data-testid="canCallCouriers">
                                        {userData?.data?.canCallCouriers === true
                                            ? t("other.yes", {ns: "common"})
                                            : t("other.no", {ns: "common"})}
                                    </span>
                                    {!["admin", "hrEmployment", "ik"].includes(
                                        userData?.data?.role
                                    ) &&
                                        userData?.data?.canCallCouriers === true && (
                                            <div
                                                style={{
                                                    color: "orange",
                                                    fontSize: "12px",
                                                    marginTop: "4px"
                                                }}
                                            >
                                                Kullanıcının rolü bu işlemi yapmaya uygun değil!
                                            </div>
                                        )}
                                </Descriptions.Item>
                                {userData?.data?.canCallCouriers === true &&
                                    userData?.data?.alotech?.queueId && (
                                        <Descriptions.Item label={"AloTech Kuyruk ID"}>
                                            <span data-testid="queueId">
                                                {userData?.data?.alotech?.queueId}
                                            </span>
                                        </Descriptions.Item>
                                    )}
                                {stores.meStore.checkRole("isAdmin") && (
                                    <Descriptions.Item
                                        label={"Referans"}
                                        contentStyle={{maxWidth: "200px"}}
                                    >
                                        <span data-testid="reference">
                                            {userData?.data?.reference
                                                ? renderReferenceInfo({
                                                      reference: userData?.data?.reference
                                                  })
                                                : "-"}
                                        </span>
                                    </Descriptions.Item>
                                )}
                                {userData?.data?.role === "subAgency" && (
                                    <Descriptions.Item label={"Yetkili Olduğu Sayfalar"}>
                                        {renderPagePermissions(
                                            userData?.data?.auth?.pagePermissions
                                        )}
                                    </Descriptions.Item>
                                )}
                            </Descriptions>
                        </Col>
                    </Row>
                    <Row type="flex" justify="end">
                        {stores.meStore.username !== userData.data.username &&
                            !isAgency &&
                            !isReadOnlyViewer && (
                                <CustomButton
                                    type={"primary"}
                                    danger
                                    data-testid="activeInActiveButton"
                                    onClick={() => {
                                        updateUserDisable(userData.data);
                                    }}
                                    icon={
                                        userData.data.disabled ? (
                                            <CheckOutlined />
                                        ) : (
                                            <CloseOutlined />
                                        )
                                    }
                                >
                                    {userData.data.disabled
                                        ? t("actions.activate", {ns: "common"})
                                        : t("actions.inactivate", {ns: "common"})}
                                </CustomButton>
                            )}
                    </Row>
                </>
            )}
        </div>
    );
};
